import { StateCreator } from "zustand";
import { ScrollAction, ScrollFrom } from "../hooks/useReadScrollToBottom";
import { ActiveConversation } from "../api/message";
export enum MessageSender {
  USER = "customer",
  AGENT = "agent",
  SYSTEM = "system",
  ROBOT = "robot",
}

export enum HeartType {
  PING = "PING",
  PONG = "PONG",
}

// 不渲染的，需要渲染的消息都放在MessageType中
export enum MessageNotRender {
  // 用户输入的实时消息
  RealTime = "message_real_time",
}

// 消息类型(注意！！ 返回的消息（新消息，历史消息）若不在当前定义的枚举内，一律隐藏)
export enum MessageType {
  // 文本消息（包含图文消息）
  Text = "text",
  // 图片消息
  Image = "image",
  // 视频消息
  Video = "video",
  // 文件消息
  File = "file",
  // 音频消息
  Audio = "audio",
  // 欢迎语
  Welcome = "message_welcome",
  // 排队
  LineUp = "message_queue",
  // 会话结束
  MsgEnd = "msgEnd",
  // 模版（扩展用）
  Template = "template",
  // 满意度模板
  Satisfy = "message_satisfy",
  // 问答（选择服务类型）
  QuestionList = "questionList",
  // 撤回消息
  Recall = "message_recall",
  // 会话已接入
  ConnectionSuccess = "connection_success",
  // 满意度已评价
  MessageSatisfyResult = "message_satisfy_result",
  // 自动回复消息，比如坐席忙碌之类
  MessageAutoReply = "message_auto_reply",
  // 问题类型
  IssueType = "issue_type",
  // 满意度已推送（存在于历史记录中）
  MessageSatisfyPushed = "message_satisfy_pushed",
  // 风控拦截
  ForbiddenMsg = "risk_control_failed",
  // 富文本消息
  RichText = "rich_text",
  // AI 消息
  AI = "ai",
}

export enum TemplateId {
  More_History = "moreHistory",
  History = "history",
  Timeout = "timeout",
  ChatEnd = "chatEnd",
}

// 消息状态
export enum MessageStatus {
  Sending = "sending",
  Failed = "failed",
  Pending = "pending",
  Success = "success",
  ERROR = "error",
  CLOSE = "close",
  Withdrawn = "withdraw",
}

// 实时会话状态,逻辑可依赖此处的状态进行逻辑判断，不可以随意增改下面的状态，特别是有交集的场景，以下的状态不可以存在交集场景
export enum CurrentChatStatus {
  // Socket 链接中
  Connecting = "connecting",
  // 开始排队
  LineUp = "message_queue",
  // 进入会话（收到人工欢迎语）
  Online = "online",
  // 会话结束(只能重新发起会话了)
  ChatEnd = "chatEnd",
  // socket 链接成功
  SocketConnected = "socketConnected",
  // 坐席不在服务
  AgentNotService = "agentNotService",
}

export enum HeaderType {
  MESSAGE = "MESSAGE",
  COMMAND = "COMMAND",
  ACK = "ACK",
  SYSTEM = "SYSTEM",
}

export enum RobotMessageType {
  ROBOT_WELCOME = "robot_welcome", // 欢迎语
  ROBOT_AI = "robot_ai", // AI
  ROBOT_RECOMMEND_QUESTION = "robot_recommend_question", // 推荐问题
  ROBOT_SSE_REPLY = "robot_reply", // 回复
  /** 历史中的 SSE 形态回复（只读静态，与实时 ROBOT_SSE_REPLY 内容格式一致） */
  ROBOT_SSE_HISTORY = "robot_sse_history",
  ROBOT_FAQ = "robot_faq", // FAQ 列表
  ROBOT_FEEDBACK = "robot_feedback", // 反馈
  ROBOT_NORMAL_REPLY = "robot_normal_reply", // 普通回复,不需要打字效果的
  ROBOT_ACTION_CARD = "robot_action_card", // 动作卡片
  ROBOT_HANDY_SELECT_HOT_QUESTION = "robot_HANDY_select_hot_question", // 选择热门问题
}

export type ScrollToBottom = (action?: ScrollAction, from?: ScrollFrom) => void;

export interface UploadFileInfo {
  file: File;
  fileId: string;
}
export interface IFeMessage {
  // 发送完消息后，可能需要禁用
  disabled?: boolean;
  // 是否隐藏，前端使用
  hide?: boolean;
  // 是否可以撤回
  canWithdraw?: boolean;
  // 重试次数
  retryCount?: number;
  // 发送状态（用于维护前端的状态）
  status?: MessageStatus;
  // 模版ID
  templateId?: TemplateId;
  // 滚动到底部
  handleScrollToBottom?: ScrollToBottom;
  // 给每条消息最外层添加一个指定className
  msgClassName?: string;
  // 禁用撤回功能
  disableWithdraw?: boolean;
  clientId?: string;
  senderName?: string;
  // 保存上传的文件信息（用于上传失败后重传）
  fileInfo?: UploadFileInfo;
  // 消息撤回
  beRecalled?: boolean;
  // 是否已读
  beRead?: boolean;
  // 是否属于新消息
  isNewMsg?: boolean;
  content?: string;
  // 消息类型
  headerType?: HeaderType;
  // 风控拦截
  riskStatus?: number;
}

// socket 返回的消息
export interface Message extends IFeMessage {
  headerType: HeaderType;
  // 消息ID，由后端生成
  id?: string;
  // 会话ID，由后端生成
  sessionId?: string;
  // 用于交换ACK确定唯一消息
  clientId?: string;
  // 消息类型
  type: MessageType | MessageNotRender;

  content: string;
  // 发送者
  senderType: MessageSender;
  // 落库时间
  timestamp: number;
  // 是否已读
  read?: boolean;
  // 客户端提交时间
  clientTimestamp?: number;
  // 会话ID
  conversationId?: string;
  // 引用消息ID
  quoteMessageId?: string;
  // 引用消息
  quoteMessage?: Message;
  // 是否被撤回
  hasRedrawn?: boolean;
  // 撤回时间
  recallTime?: number;
  // 表示是不是handy侧的hms消息，如果是，则不展示在消息列表中
  handyHmsOnly?: boolean;
}

export enum SocketConnectionStatus {
  CONNECTED = "connected",
  CONNECTING = "connecting",
  // SOCKET 出错了，这种情况下来源于socket.onerror 不尝试重连，直接异常了
  ERROR = "error",
  // 断掉了，不重连了（可能来源于主动断掉，也可能被动断掉）
  DISCONNECTED = "disconnected",
  // 断掉，尝试重连, 情况很多种，可能触发重连（不知道的异常断开），也可能不触发重连（知道断开的原因，业务场景需要）
  CLOSE = "close",
}
export interface LastHistoryMsg {
  nextCursor: string;
  hasMore: boolean;
}

export enum SocketErrorInfoType {
  TEXT = 1,
  GLOBAL = 2,
}
export interface SocketErrorInfo {
  type: SocketErrorInfoType;
  message: string;
}
export interface MessageActions {
  markMessageAsRead: (messageId: string) => void;
  resetUnreadCount: () => void;
  getUnreadMessages: () => Message[];

  // 添加新消息（用户发送）
  addOutgoingMessage: (msg: Message) => Message;

  // 接收新消息（服务端推送）
  addIncomingMessage: (message: Message) => void;

  // 更新消息状态
  updateMessageStatus: (
    id: string,
    opts: IFeMessage,
    serverId?: string,
  ) => void;

  // 处理消息发送结果
  handleSendResult: (
    tempId: string,
    success: boolean,
    serverId?: string,
  ) => void;

  // 重试发送消息
  retrySendMessage: (message: Message) => void;

  // 连接状态管理
  setConnectionStatus: (status: SocketConnectionStatus) => void;

  setCurrentChatStatus: (status: CurrentChatStatus) => void;

  // 清空消息状态
  reset: () => void;

  // 撤回消息
  withdrawMessage: (messageId: string, msgSender: MessageSender) => void;

  // 历史消息部分
  // 历史记录是否已加载完成
  historyLoaded: boolean;
  // 初始化历史数据
  initializeHistory: (messages: Message[]) => void;

  // 保存上一次请求的历史记录
  setLastHistoryMsg: (msg: LastHistoryMsg) => void;

  // 插入历史记录
  insertHistory: (messages: Message[]) => void;

  setBottomReachedStatus: (status: boolean) => void;

  setCommonMessage: (msg: Message) => Message;
  // 设置引用消息
  setQuotedMsg: (quotedMsg?: Message) => void;

  // 重新编辑：撤回消息后填入输入框的内容，由 ChatInput 消费后置空
  setReEditContent: (content: string | null) => void;

  setSocketErrorInfo: (socketErrorInfo: SocketErrorInfo) => void;

  setSessionInfo: (sessionInfo: ActiveConversation) => void;

  setRobotMessage: (message: RobotMessage) => void;

  updateRobotMessage: (messageId: string, opts: Partial<RobotMessage>) => void;

  // 设置 AI 打字机效果是否结束
  setAiTypingEnd: (typingEnd: boolean) => void;

  // 设置全局 SSE 停止函数
  setGlobalStopStreaming: (stopFn: (() => void) | null) => void;

  // 设置 AI SSE 流是否正在进行中
  setIsAIStreaming: (streaming: boolean) => void;
}

export interface MessageState {
  // 历史消息（初始化加载）
  historyMsgList: Message[];

  // 实时消息队列（初始为空）
  msgList: (Message | RobotMessage)[];

  chatStatus: CurrentChatStatus | null;

  // 连接状态
  isConnected: SocketConnectionStatus;

  // 消息未读数
  unreadCount: number;

  // 上一次的历史记录信息
  lastHistoryMsg: LastHistoryMsg;

  // 是否到达底部，这个依赖往外扩的值，并非完全触底
  bottomReached?: boolean;

  // 会话ID
  conversationId?: string;

  // 引用的消息
  quotedMsg?: Message;

  // 用户是否已经发送过第一条消息
  hasUserSentFirstMessage?: boolean;

  // 重新编辑内容（撤回后点击「重新编辑」时填入输入框）
  reEditContent: string | null;

  socketErrorInfo?: SocketErrorInfo;

  sessionInfo?: ActiveConversation;

  // AI 打字机效果是否结束
  aiTypingEnd: boolean;

  // 全局 SSE 停止函数（用于跨组件停止 SSE 流）
  globalStopStreaming: (() => void) | null;

  // AI SSE 流是否正在进行中（全局，跨组件共享）
  isAIStreaming: boolean;
}

export type MessageStore = MessageState & MessageActions;

export type MessageStoreCreator = StateCreator<
  MessageStore,
  [["zustand/devtools", never], ["zustand/subscribeWithSelector", never]]
>;

export interface SendMessageOptions {
  msgType?: MessageType | MessageNotRender;
  // 不需要插入到消息列表
  noNeedInsertToMsgList?: boolean;
  // 不需要推送给websocket 服务
  noNeedSendToWebsocket?: boolean;
  senderType?: MessageSender;
}

type User = {
  id: string;
  name: string;
  account: string;
  email: string;
  phone: string;
};

export type UserState = {
  user: User | null;
  fetchUserInfo: () => Promise<void>;
};
export interface ScrollChatStore {
  bottomReached: boolean;
  setBottomReached: (status: boolean) => void;
}

export type ScrollChatStoreCreator = StateCreator<
  ScrollChatStore,
  [["zustand/devtools", never], ["zustand/subscribeWithSelector", never]]
>;

export interface IObject {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // 允许任意键值对
}

// 定义上报数据的类型
export interface ReportData {
  // 额外的数据
  extra?: string;
  scene: string; // 必须包含事件类型
  // 当前上报的时间
  t?: number;
  // 每次刷新页面生成的唯一traceId
  traceId?: string;
  // 用户ID
  cusId?: string;
  // 会话ID
  conversationId?: string;
  // 聊天状态
  chatStatus?: CurrentChatStatus | null;
  // 链接状态
  isConnected?: SocketConnectionStatus;
}

export interface BannerInfo {
  type: "info" | "danger" | "warning" | "success";
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}

export enum RiskStatus {
  Not_Checked = 0,
  Checking = 1,
  Pass = 2,
  Failed = 3,
}
export interface RobotMessage extends Message {
  content: string;
  senderType: MessageSender;
  timestamp: number;
  messageType: RobotMessageType;
  showFeedFooter?: boolean;
  streaming?: boolean;
  error?: boolean; // 标记消息是否处于错误状态（可重试）
  robotData: {
    // 机器人相关数据
    [key: string]: unknown;
  };
}
